import { Card, Suit, Rank } from '../../state/ArenaTypes';
import {
  createStandardDeck,
  getSuitSymbol,
  getSuitColor,
  getCardById,
  normalizeSuit,
  toShortSuitCode,
  areSuitsEqual,
  isRedSuit,
} from '../../engine/StandardDeck';
import { HeartsQueenOfSpadesPortrait, HeartsQueenOfSpadesPortraitProps } from './presentation/HeartsQueenOfSpadesPortrait';

export {
  HeartsQueenOfSpadesPortrait,
  getSuitSymbol,
  getSuitColor,
  getCardById,
};
export type { HeartsQueenOfSpadesPortraitProps };

/**
 * Creates a standard 52-card Hearts deck (strictly 52 cards without jokers).
 * Queen of Spades (S_Q) carries the 13 penalty points or moon shoot payload.
 */
export function createHeartsDeck(): Card[] {
  return createStandardDeck();
}

/**
 * Checks if a card is the penalty Queen of Spades in Hearts.
 */
export function isHeartsQueenOfSpades(card?: Card | null): boolean {
  if (!card) return false;
  return (
    card.id === 'S_Q' ||
    card.id === 'QS' ||
    ((card.suit === 'S' || card.suit === 'Spades') && (card.rank === 'Q' || (card.rank as string) === 'Queen'))
  );
}

/**
 * Checks if a card is any penalty card in Hearts (any Heart or the Queen of Spades).
 */
export function isHeartsPenaltyCard(card?: Card | null): boolean {
  if (!card) return false;
  if (isHeartsQueenOfSpades(card)) return true;
  return card.suit === 'H' || card.suit === 'Hearts' || card.id?.startsWith('H_') || card.id?.endsWith('H');
}

/**
 * Checks if a card is the bonus Jack of Diamonds in Hearts (-10 points variant).
 */
export function isHeartsJackOfDiamonds(card?: Card | null): boolean {
  if (!card) return false;
  const id = (card.id || '').toUpperCase();
  const suit = (card.suit || '').toUpperCase();
  const rank = (card.rank || '').toString().toUpperCase();
  return (
    id === 'D_J' ||
    id === 'JD' ||
    id === 'DJ' ||
    ((suit === 'D' || suit === 'DIAMONDS' || suit === '♦') && (rank === 'J' || rank === 'JACK' || rank === '11'))
  );
}

/**
 * Gets the point value of a card in Hearts:
 * - Queen of Spades: 13 points
 * - Any Heart: 1 point
 * - Jack of Diamonds (when enabled): -10 points
 * - Other cards: 0 points
 */
export function getHeartsCardPointValue(card?: Card | null, isJackOfDiamondsEnabled: boolean = false): number {
  if (!card) return 0;
  if (isHeartsQueenOfSpades(card)) return 13;
  if (card.suit === 'H' || card.suit === 'Hearts' || card.id?.startsWith('H_')) return 1;
  if (isJackOfDiamondsEnabled && isHeartsJackOfDiamonds(card)) {
    return -10;
  }
  return 0;
}

